import React from 'react';
import SEO from '../components/SEO';
import PageHero, { CTABanner } from '../components/PageHero';
import { clients } from '../data/mock';
import { Building2, ShieldCheck, Award, Globe } from 'lucide-react';

export default function ClientsPage() {
  return (
    <>
      <SEO
        title="Clients & Experience"
        description="Selected experience with leading enterprises, public institutions, and development organizations including the World Bank Group, African Development Bank, and the Ministry of Finance."
        path="/clients"
        keywords="Salihi Consulting clients, World Bank Somalia, African Development Bank, Ministry of Finance Somalia, institutional clients"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Clients & Experience — Salihi Consulting',
          url: 'https://salihiconsulting.com/clients',
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: clients.map((c, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              item: { '@type': 'Organization', name: c.name, description: c.desc },
            })),
          },
        }}
      />
      <PageHero
        breadcrumb="Clients & Experience"
        eyebrow="Trusted relationships"
        title="Selected clients &"
        italicTitle="institutional partners."
        subtitle="Over the years, Salihi Consulting Services has had the privilege of advising and delivering for a portfolio of leading enterprises, public institutions, and international development organizations. The relationships listed below represent a selection of our experience."
      />

      {/* Intro paragraph */}
      <section className="py-20 sm:py-24 bg-[#0d2143]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-3 gap-5 mb-14">
            {[
              { icon: Building2, title: 'Private enterprise', desc: 'From family-owned businesses to major regional conglomerates, we are trusted advisors to commercial organizations of every scale.' },
              { icon: ShieldCheck, title: 'Public institutions', desc: 'We have supported ministries, regulators, and public agencies on reform, capacity building and operational improvement.' },
              { icon: Globe, title: 'Development partners', desc: 'Engagements with multilateral development institutions and donor-funded programs across multiple sectors.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 rounded-2xl p-7">
                <div className="w-12 h-12 rounded-xl bg-[#e8b863]/12 border border-[#e8b863]/30 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-[#e8b863]" />
                </div>
                <h3 className="font-serif text-white text-[22px] mb-3">{title}</h3>
                <p className="text-white/72 text-[14.5px] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mb-10">
            <p className="text-[#e8b863] tracking-[0.28em] text-[11px] font-semibold uppercase mb-4">Selected experience</p>
            <h2 className="font-serif text-white font-medium text-[28px] sm:text-[36px] lg:text-[42px] leading-[1.1] tracking-tight">
              Organizations we have worked with and supported.
            </h2>
            <p className="mt-5 text-white/72 text-[15px] leading-relaxed">
              The following organizations represent some of the clients and partners
              associated with our engagements. Many of our other engagements remain
              confidential at the request of the client.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {clients.map((c) => (
              <div key={c.name} className="group bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 rounded-2xl p-6 hover:border-[#e8b863]/40 hover:-translate-y-0.5 transition-all duration-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#122a52] to-[#1a3a6b] border border-[#e8b863]/30 flex items-center justify-center text-[#e8b863] font-serif font-semibold text-[18px] flex-shrink-0">
                    {c.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-white font-semibold text-[15px] leading-snug truncate">{c.name}</h3>
                    <p className="text-[#e8b863] text-[11px] tracking-[0.15em] uppercase font-semibold mt-0.5">{c.sector}</p>
                  </div>
                </div>
                <p className="text-white/68 text-[13.5px] leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-center text-white/55 text-[12.5px] tracking-[0.18em] uppercase">
            and many more confidential engagements
          </p>
        </div>
      </section>

      {/* Why clients work with us */}
      <section className="py-20 sm:py-24 bg-[#0a1b35]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-[#e8b863] tracking-[0.28em] text-[11px] font-semibold uppercase mb-5">Why they choose us</p>
            <h2 className="font-serif text-white font-medium text-[28px] sm:text-[36px] lg:text-[42px] leading-[1.1] tracking-tight">
              The qualities our clients return for.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { title: 'Senior-led delivery', desc: 'The people who scope the engagement are the same people who deliver it. Clients deal with decision-makers, not handoffs.' },
              { title: 'Discretion as default', desc: 'Confidentiality is wired into how we recruit, work, and report. Many of our engagements are never disclosed publicly.' },
              { title: 'Local context, disciplined practice', desc: 'We combine on-the-ground understanding of Somalia and the region with structured, evidence-based methods.' },
              { title: 'Capacity that outlasts the engagement', desc: 'We embed knowledge and tools with client teams so improvements continue compounding after we leave.' },
            ].map((b) => (
              <div key={b.title} className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-2xl p-7 hover:border-[#e8b863]/40 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Award className="w-5 h-5 text-[#e8b863]" />
                  <h3 className="text-white font-semibold text-[18px]">{b.title}</h3>
                </div>
                <p className="text-white/72 text-[14.5px] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner title="Become our next long-term client." />
    </>
  );
}
